import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  backfillCandidateNotesCvs,
  backfillEmployeeDocuments,
  getBackfillStatus,
  getCandidates,
  getDocumentsBackfillStatus,
  getPeopleForceStatus,
  getRecruitmentCounts,
  getSyncHistory,
  getVacancies,
  syncAbsenceBalances,
  syncAbsenceTypes,
  syncAvatars,
  pullAvatarsFromPf,
  syncCalendar,
  syncCandidates,
  syncCompensations,
  syncAdditionalCompensations,
  syncEmployees,
  syncProjects,
  syncVacancies,
  type BackfillCandidateNotesCvsResponse,
  type BackfillProgressSnapshot,
  type CandidatePreview,
  type DocumentBackfillProgress,
  type SyncHistoryEntry,
  type SyncResult,
  type VacancyPreview,
} from '@/lib/peopleforce';
import { IntegrationNoAccess } from '@/components/integration-no-access';
import { formatDate } from '@/lib/date';
import { extractErrorMessage } from '@/lib/errors';
import { usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  ArrowLeft,
  Briefcase,
  Calendar,
  CalendarClock,
  CheckCircle2,
  DollarSign,
  Eye,
  FolderKanban,
  Image,
  Loader2,
  RefreshCw,
  UserSearch,
  Users,
  Wifi,
  WifiOff,
  Search,
  Tags,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';

const routePath = '/settings/integrations/peopleforce' as const;
export const Route = createFileRoute(routePath)({
  component: PeopleForceGate,
});

function PeopleForceGate() {
  // PeopleForce is admin-managed — keep LinkedIn-only recruiters out (direct URL).
  const canManage = usePermission('settings.general.manage');
  if (!canManage) return <IntegrationNoAccess />;
  return <PeopleForceSyncPage />;
}

interface SyncAction {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  mutationFn: () => Promise<SyncResult>;
  syncTypes: string[];
  hasPreview?: boolean;
  destructive?: boolean;
  confirmTitle?: string;
  confirmDescription?: string;
}

const syncActions: SyncAction[] = [
  {
    id: 'employees',
    title: 'Employees',
    description:
      'Sync all employees including positions, departments, personal info, and manager relationships',
    icon: Users,
    mutationFn: syncEmployees,
    syncTypes: ['employees_full_sync', 'employee_import'],
  },
  {
    id: 'avatars',
    title: 'Avatars',
    description: 'Download employee photos from PeopleForce and save them locally',
    icon: Image,
    mutationFn: syncAvatars,
    syncTypes: ['avatar_migration'],
  },
  {
    id: 'avatars-pull-from-pf',
    title: 'Avatars · Recovery from PF',
    description:
      "Force-pull every avatar that's missing locally — null in DB, still pointing at PeopleForce, or whose backing file went missing on disk. Run this before PeopleForce access is cut so nothing keeps depending on PF infrastructure.",
    icon: Image,
    mutationFn: pullAvatarsFromPf,
    syncTypes: ['avatars_pull'],
  },
  {
    id: 'calendar',
    title: 'Calendar',
    description: 'Sync approved leave requests and birthdays for the current month',
    icon: Calendar,
    mutationFn: syncCalendar,
    syncTypes: ['calendar_sync'],
  },
  {
    id: 'compensations',
    title: 'Compensations',
    description: 'Sync salary history for all employees from PeopleForce',
    icon: DollarSign,
    mutationFn: syncCompensations,
    syncTypes: ['compensations_sync'],
  },
  {
    id: 'additional-compensations',
    title: 'Additional Compensation (Bonuses)',
    description: 'Sync bonuses, stipends and one-time payments from PeopleForce',
    icon: DollarSign,
    mutationFn: syncAdditionalCompensations,
    syncTypes: ['additional_compensations_sync'],
  },
  {
    id: 'projects',
    title: 'Projects',
    description: 'Sync teams/projects and their members from PeopleForce',
    icon: FolderKanban,
    mutationFn: syncProjects,
    syncTypes: ['projects_sync'],
  },
  {
    id: 'vacancies',
    title: 'Vacancies',
    description: 'Sync recruitment vacancies from PeopleForce',
    icon: Briefcase,
    mutationFn: syncVacancies,
    syncTypes: ['vacancies_sync'],
    hasPreview: true,
  },
  {
    id: 'candidates',
    title: 'Candidates',
    description: 'Sync recruitment candidates and applications. Sync vacancies first.',
    icon: UserSearch,
    mutationFn: syncCandidates,
    syncTypes: ['candidates_sync'],
    hasPreview: true,
  },
  {
    id: 'absence_types',
    title: 'Absence Types',
    description:
      'Sync leave-type definitions from PeopleForce. Existing local types are linked by name; new types are created as shells (HR configures accrual rules afterwards). Run this BEFORE syncing balances.',
    icon: Tags,
    mutationFn: syncAbsenceTypes,
    syncTypes: ['absence_types_sync'],
  },
  {
    id: 'absence_balances',
    title: 'Absence Balances (Migration)',
    description:
      "One-time import of leave balances from PeopleForce. Sets each balance to PF's available days and resets used/pending counters. After this, the local accrual configurator continues forward from the import date.",
    icon: CalendarClock,
    mutationFn: syncAbsenceBalances,
    syncTypes: ['absence_balance_sync'],
    destructive: true,
    confirmTitle: 'Import absence balances from PeopleForce?',
    confirmDescription:
      'This will overwrite current accrued/used/pending/carried-over days for every employee × absence type matched to PeopleForce. The local configurator will use these numbers as the new starting point. Audit-logged. Re-running this will overwrite again.',
  },
];

function PeopleForceSyncPage() {
  const queryClient = useQueryClient();
  const [previewType, setPreviewType] = useState<'vacancies' | 'candidates' | null>(null);

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['peopleforce', 'status'],
    queryFn: getPeopleForceStatus,
    retry: false,
  });

  const { data: history = [] } = useQuery({
    queryKey: ['peopleforce', 'history'],
    queryFn: () => getSyncHistory(20),
    refetchInterval: 10000,
  });

  const isConfigured = status?.configured && status?.success;

  return (
    <div className="flex-1 overflow-auto">
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        <div className="space-y-4">
          <Link
            to="/settings"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Settings
          </Link>

          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              PeopleForce Sync
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Synchronize data from PeopleForce into HyggeOS
            </p>
          </div>
        </div>

        <ConnectionStatus status={status} isLoading={statusLoading} />

        {isConfigured && (
          <>
            <div className="space-y-3">
              {syncActions.map((action) => (
                <SyncActionCard
                  key={action.id}
                  action={action}
                  history={history}
                  onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['peopleforce', 'history'] });
                    queryClient.invalidateQueries({
                      queryKey: ['peopleforce', 'recruitment-counts'],
                    });
                  }}
                  onPreview={
                    action.hasPreview
                      ? () => setPreviewType(action.id as 'vacancies' | 'candidates')
                      : undefined
                  }
                />
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                <span>Migration tools (one-shot)</span>
                <span className="h-px flex-1 bg-border" />
              </div>
              <BackfillCandidatesCard
                history={history}
                onSuccess={() => {
                  queryClient.invalidateQueries({ queryKey: ['peopleforce', 'history'] });
                  // Bulk-invalidate every open candidate's notes + files panes
                  // so anyone already viewing a profile when the backfill ends
                  // sees the freshly-imported rows without a manual reload.
                  // candidateKeys.all = ['recruitment', 'candidates']
                  // notes / recentNotes / files queries: [...all, kind, id]
                  queryClient.invalidateQueries({
                    predicate: (q) => {
                      const k = q.queryKey;
                      return (
                        Array.isArray(k) &&
                        k.length >= 3 &&
                        k[0] === 'recruitment' &&
                        k[1] === 'candidates' &&
                        (k[2] === 'notes' || k[2] === 'recentNotes' || k[2] === 'files')
                      );
                    },
                  });
                }}
              />
              <BackfillDocumentsCard
                history={history}
                onSuccess={() => {
                  queryClient.invalidateQueries({ queryKey: ['peopleforce', 'history'] });
                  // Invalidate every open employee profile's Documents tab so
                  // fresh PF imports show up without a hard refresh. The
                  // useDocuments hook keys are: ['employees', 'documents', employeeId]
                  // — best-effort predicate match.
                  queryClient.invalidateQueries({
                    predicate: (q) => {
                      const k = q.queryKey;
                      return (
                        Array.isArray(k) &&
                        k.length >= 2 &&
                        k[0] === 'employees' &&
                        (k[1] === 'documents' || k[1] === 'document')
                      );
                    },
                  });
                }}
              />
            </div>

            <SyncHistorySection history={history} />
          </>
        )}

        {!isConfigured && <RecruitmentDataSection onPreview={(type) => setPreviewType(type)} />}
      </div>

      {previewType === 'vacancies' && (
        <VacanciesPreviewDialog open onOpenChange={(open) => !open && setPreviewType(null)} />
      )}
      {previewType === 'candidates' && (
        <CandidatesPreviewDialog open onOpenChange={(open) => !open && setPreviewType(null)} />
      )}
    </div>
  );
}

function ConnectionStatus({
  status,
  isLoading,
}: {
  status: ReturnType<typeof useQuery<Awaited<ReturnType<typeof getPeopleForceStatus>>>>['data'];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Checking connection...</span>
        </CardContent>
      </Card>
    );
  }

  if (!status?.configured) {
    return (
      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardContent className="flex items-center gap-3 py-4">
          <WifiOff className="h-5 w-5 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
              PeopleForce API not configured
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Set the PF_API_KEY environment variable on the backend to enable sync
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!status.success) {
    return (
      <Card className="border-destructive/30 bg-destructive/5">
        <CardContent className="flex items-center gap-3 py-4">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <div>
            <p className="text-sm font-medium text-destructive">Connection failed</p>
            <p className="text-xs text-muted-foreground mt-0.5">{status.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-green-500/30 bg-green-500/5">
      <CardContent className="flex items-center gap-3 py-4">
        <Wifi className="h-5 w-5 text-green-600" />
        <div>
          <p className="text-sm font-medium text-green-700 dark:text-green-400">
            Connected to PeopleForce
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {status.employeeCount} employees available in PeopleForce
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function SyncActionCard({
  action,
  history,
  onSuccess,
  onPreview,
}: {
  action: SyncAction;
  history: SyncHistoryEntry[];
  onSuccess: () => void;
  onPreview?: () => void;
}) {
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: action.mutationFn,
    meta: { suppressGlobalToast: true },
    onSuccess: (result) => {
      setLastResult(result);
      onSuccess();
    },
  });

  const triggerSync = () => {
    setLastResult(null);
    if (action.destructive) {
      setConfirmOpen(true);
    } else {
      mutation.mutate();
    }
  };

  const lastSync = history.find((h) => action.syncTypes.includes(h.syncType));
  const Icon = action.icon;

  return (
    <>
      <Card className={cn(mutation.isPending && 'border-hygge/30')}>
        <CardContent className="flex items-center gap-4 py-4">
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors',
              mutation.isPending ? 'bg-hygge/20' : 'bg-hygge/10'
            )}
          >
            {mutation.isPending ? (
              <Loader2 className="h-5 w-5 text-hygge animate-spin" />
            ) : (
              <Icon className="h-5 w-5 text-hygge" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm">{action.title}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{action.description}</p>

            {lastSync && !mutation.isPending && !lastResult && (
              <p className="text-xs text-muted-foreground mt-1">
                Last sync: {formatRelativeTime(lastSync.syncedAt)}
                {lastSync.status === 'success' && (
                  <span className="text-green-600 ml-1">({lastSync.recordsProcessed} records)</span>
                )}
                {lastSync.status === 'partial' && (
                  <span className="text-amber-600 ml-1">
                    ({lastSync.recordsProcessed} records, {lastSync.recordsFailed} errors)
                  </span>
                )}
              </p>
            )}

            {mutation.isPending && (
              <p className="text-xs text-hygge mt-1">
                Synchronizing... This may take a few minutes.
              </p>
            )}

            {lastResult && !mutation.isPending && (
              <div className="flex items-center gap-1.5 mt-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                <p className="text-xs text-green-600">{lastResult.message}</p>
              </div>
            )}

            {mutation.isError && !mutation.isPending && (
              <div className="flex items-center gap-1.5 mt-1">
                <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                <p className="text-xs text-destructive">
                  {extractErrorMessage(mutation.error, 'Sync failed')}
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {onPreview && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 cursor-pointer text-muted-foreground hover:text-foreground"
                onClick={onPreview}
              >
                <Eye className="h-3.5 w-3.5" />
                Preview
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 cursor-pointer"
              disabled={mutation.isPending}
              onClick={triggerSync}
            >
              <RefreshCw className={cn('h-3.5 w-3.5', mutation.isPending && 'animate-spin')} />
              {mutation.isPending ? 'Syncing...' : 'Sync'}
            </Button>
          </div>
        </CardContent>
      </Card>
      {action.destructive && (
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <AlertDialogTitle>{action.confirmTitle}</AlertDialogTitle>
              </div>
              <AlertDialogDescription>{action.confirmDescription}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setConfirmOpen(false);
                  mutation.mutate();
                }}
              >
                Yes, import
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

/**
 * One-shot migration card. Custom (not part of the generic SyncActionCard
 * grid) because it takes a `monthsBack` lookback parameter — every other
 * card is parameter-less. Visually distinct via the `Migration tools`
 * section header above it and the hygge-tinted border below.
 *
 * Polls `GET /peopleforce/backfill-status` every 3 s while a backfill is
 * running so the user sees a live progress bar with throughput + ETA
 * instead of a 1-3 hour blank spinner.
 */
function BackfillCandidatesCard({
  history,
  onSuccess,
}: {
  history: SyncHistoryEntry[];
  onSuccess: () => void;
}) {
  const [monthsBack, setMonthsBack] = useState(6);
  const [lastResult, setLastResult] = useState<BackfillCandidateNotesCvsResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: () => backfillCandidateNotesCvs({ monthsBack }),
    meta: { suppressGlobalToast: true },
    onSuccess: (result) => {
      setLastResult(result);
      onSuccess();
    },
  });

  // Poll live progress. Always-on while mounted (cheap, in-memory) so we
  // pick up a backfill that some other tab/curl started, not just our own.
  // Tighten cadence while running (3 s); stretch when idle (15 s).
  const { data: progress } = useQuery<BackfillProgressSnapshot | null>({
    queryKey: ['peopleforce', 'backfillProgress'],
    queryFn: getBackfillStatus,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 3000 : 15000),
    refetchIntervalInBackground: true,
  });

  // When polling catches a 'running' → 'completed' / 'partial' transition,
  // fire `onSuccess` once so the parent invalidates the candidate notes /
  // files queries. Covers the case where the user opened the Settings tab
  // *after* a backfill (started elsewhere, or via curl) had already begun.
  const prevStatusRef = useRef<BackfillProgressSnapshot['status'] | null>(null);
  useEffect(() => {
    const cur = progress?.status ?? null;
    const prev = prevStatusRef.current;
    if (prev === 'running' && (cur === 'completed' || cur === 'partial')) {
      onSuccess();
    }
    prevStatusRef.current = cur;
  }, [progress?.status, onSuccess]);

  const isRunning = progress?.status === 'running' || mutation.isPending;
  const lastSync = history.find((h) => h.syncType === 'candidates_backfill_notes_cvs');

  return (
    <>
      <Card className={cn('border-hygge/40 bg-hygge/5', isRunning && 'border-hygge/70')}>
        <CardHeader className="pb-2">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors',
                isRunning ? 'bg-hygge/30' : 'bg-hygge/15'
              )}
            >
              {isRunning ? (
                <Loader2 className="h-5 w-5 text-hygge animate-spin" />
              ) : (
                <Archive className="h-5 w-5 text-hygge" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                Candidate Notes & CVs Backfill
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Pulls PF notes and downloads CV PDFs for candidates updated within the lookback
                window. Idempotent — re-runs skip CVs already imported and never overwrite
                locally-edited notes.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-2">
          <div className="flex items-center gap-2 flex-wrap">
            <label
              htmlFor="months-back"
              className="text-xs font-medium text-muted-foreground whitespace-nowrap"
            >
              Lookback window
            </label>
            <Input
              id="months-back"
              type="number"
              min={1}
              max={60}
              step={1}
              value={monthsBack}
              onChange={(e) =>
                setMonthsBack(Math.max(1, Math.min(60, Number(e.target.value) || 6)))
              }
              className="h-8 w-20 text-sm"
              disabled={isRunning}
            />
            <span className="text-xs text-muted-foreground">months</span>
            <Button
              size="sm"
              className="ml-auto gap-1.5 cursor-pointer"
              disabled={isRunning}
              onClick={() => {
                setLastResult(null);
                setConfirmOpen(true);
              }}
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isRunning && 'animate-spin')} />
              {isRunning ? 'Backfilling…' : 'Start backfill'}
            </Button>
          </div>

          {/* Live progress bar — visible while backend reports `running`. */}
          {progress && progress.status === 'running' && <BackfillProgressBar progress={progress} />}

          {/* Show last-completed snapshot from server (survives page reload). */}
          {progress &&
            (progress.status === 'completed' || progress.status === 'partial') &&
            !lastResult && <BackfillCompletedSummary progress={progress} />}

          {lastSync && !isRunning && !lastResult && !progress && (
            <p className="text-xs text-muted-foreground">
              Last run: {formatRelativeTime(lastSync.syncedAt)}
              {lastSync.status === 'success' && (
                <span className="text-green-600 ml-1">({lastSync.recordsProcessed} records)</span>
              )}
              {lastSync.status === 'partial' && (
                <span className="text-amber-600 ml-1">
                  ({lastSync.recordsProcessed} records, {lastSync.recordsFailed} errors)
                </span>
              )}
            </p>
          )}

          {lastResult && !mutation.isPending && (
            <div className="space-y-1">
              <div className="flex items-start gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
                <p className="text-xs text-green-700 dark:text-green-400 font-medium">
                  {lastResult.message}
                </p>
              </div>
              {lastResult.data.errorMessages.length > 0 && (
                <details className="text-xs text-amber-700 dark:text-amber-400 ml-5">
                  <summary className="cursor-pointer hover:underline">
                    {lastResult.data.errorMessages.length} per-record error
                    {lastResult.data.errorMessages.length === 1 ? '' : 's'} (showing first 10)
                  </summary>
                  <ul className="mt-1 ml-4 list-disc space-y-0.5">
                    {lastResult.data.errorMessages.slice(0, 10).map((msg, i) => (
                      <li key={i} className="font-mono text-[11px]">
                        {msg}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {mutation.isError && !mutation.isPending && (
            <div className="flex items-start gap-1.5">
              <AlertCircle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
              <p className="text-xs text-destructive">
                {extractErrorMessage(mutation.error, 'Backfill failed')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-hygge" />
              <AlertDialogTitle>
                Backfill last {monthsBack} {monthsBack === 1 ? 'month' : 'months'} from PeopleForce?
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  For every candidate whose PF activity falls within the last{' '}
                  <strong>{monthsBack}</strong> {monthsBack === 1 ? 'month' : 'months'}, this will:
                </p>
                <ul className="ml-5 list-disc space-y-1">
                  <li>Pull all notes from PF and upsert into local DB (rich HTML preserved).</li>
                  <li>
                    Download the resume PDF and store it as a CV file — skipped if a
                    PeopleForce-sourced CV already exists.
                  </li>
                </ul>
                <p className="text-muted-foreground">
                  Runs on the server — safe to navigate away. Notes you've edited locally are never
                  overwritten. Re-running is safe (idempotent).
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                mutation.mutate();
              }}
            >
              Start backfill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Format milliseconds as "Xh Ym Zs" for the ETA / elapsed labels. */
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/**
 * Live progress bar fed by `progress` snapshot. ETA is derived client-side
 * from `(processed / durationMs) * remaining` — server only ships raw
 * counters so the UI can re-compute on every refresh without staleness.
 */
function BackfillProgressBar({ progress }: { progress: BackfillProgressSnapshot }) {
  const {
    processed,
    total,
    durationMs,
    cvsCreated,
    cvsSkipped,
    notesCreated,
    notesUpdated,
    cvsErrors,
    notesErrors,
    currentCandidate,
    startedAt,
  } = progress;

  const pct = total > 0 ? Math.min(100, (processed / total) * 100) : 0;
  const rateMs = processed > 0 ? durationMs / processed : 0;
  const remaining = Math.max(0, total - processed);
  const etaMs = rateMs > 0 ? rateMs * remaining : 0;
  const candidatesPerMin = rateMs > 0 ? Math.round(60_000 / rateMs) : 0;
  const elapsedMs = Date.now() - new Date(startedAt).getTime();

  return (
    <div className="space-y-2 rounded-md border border-hygge/30 bg-background/60 p-3">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-foreground">
          {processed.toLocaleString()}
          <span className="text-muted-foreground"> / {total.toLocaleString()} candidates</span>
        </span>
        <span className="tabular-nums font-medium text-hygge">{pct.toFixed(1)}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-hygge/10">
        <div
          className="h-full bg-hygge transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
        <div>
          <div className="text-foreground tabular-nums">{formatDuration(elapsedMs)}</div>
          <div>elapsed</div>
        </div>
        <div>
          <div className="text-foreground tabular-nums">
            {etaMs > 0 ? formatDuration(etaMs) : '—'}
          </div>
          <div>ETA</div>
        </div>
        <div>
          <div className="text-foreground tabular-nums">
            {candidatesPerMin > 0 ? `${candidatesPerMin}/min` : '—'}
          </div>
          <div>throughput</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
          <span className="tabular-nums text-foreground">{cvsCreated}</span>
          <span className="text-muted-foreground">CVs created</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
          <span className="tabular-nums text-foreground">{cvsSkipped}</span>
          <span className="text-muted-foreground">skipped</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-hygge" />
          <span className="tabular-nums text-foreground">{notesCreated}</span>
          <span className="text-muted-foreground">notes new</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-hygge/60" />
          <span className="tabular-nums text-foreground">{notesUpdated}</span>
          <span className="text-muted-foreground">updated</span>
        </span>
        {(cvsErrors > 0 || notesErrors > 0) && (
          <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3" />
            <span className="tabular-nums">{cvsErrors + notesErrors}</span>
            <span>errors (per-record, isolated)</span>
          </span>
        )}
      </div>
      {currentCandidate && (
        <div className="truncate text-[11px] text-muted-foreground">
          Currently: <span className="text-foreground">{currentCandidate.fullName}</span>
          {currentCandidate.externalId && (
            <span className="text-muted-foreground"> · pf:{currentCandidate.externalId}</span>
          )}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        Safe to leave this page — the job continues on the server and this bar resumes when you come
        back.
      </p>
    </div>
  );
}

/**
 * Shown when the server reports a completed (or partial) backfill but the
 * user's own click didn't land it (page reload, different tab, etc.).
 */
function BackfillCompletedSummary({ progress }: { progress: BackfillProgressSnapshot }) {
  const total = progress.cvsCreated + progress.notesCreated + progress.notesUpdated;
  const errCount = progress.cvsErrors + progress.notesErrors;
  const isPartial = progress.status === 'partial';
  return (
    <div className="space-y-1">
      <div className="flex items-start gap-1.5">
        <CheckCircle2
          className={cn(
            'mt-0.5 h-3.5 w-3.5 shrink-0',
            isPartial ? 'text-amber-600' : 'text-green-600'
          )}
        />
        <p
          className={cn(
            'text-xs font-medium',
            isPartial ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'
          )}
        >
          Backfill {isPartial ? 'finished with errors' : 'completed'}: {progress.cvsCreated} CVs
          created, {progress.cvsSkipped} skipped. {progress.notesCreated} notes created,{' '}
          {progress.notesUpdated} updated. Scanned {progress.processed.toLocaleString()} candidates
          over {formatDuration(progress.durationMs)} ({total} writes
          {errCount > 0 && `, ${errCount} per-record errors`}).
        </p>
      </div>
    </div>
  );
}

/**
 * Bulk employee-documents backfill card. No `monthsBack` knob — we sync
 * everything for every PF-linked employee, encrypted on the fly. Live
 * progress mirrors `BackfillCandidatesCard` (poll `/documents-backfill-status`
 * every 3s while running, fall back to 15s when idle).
 *
 * Files imported here land as ciphertext on the EC2 disk (and later S3) —
 * readable only via `DocumentsService.download()` after RBAC check. KMS
 * `Decrypt` is gated to the EC2 app role only, so ops/devops with full S3
 * access still cannot read content.
 */
function BackfillDocumentsCard({
  history,
  onSuccess,
}: {
  history: SyncHistoryEntry[];
  onSuccess: () => void;
}) {
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: () => backfillEmployeeDocuments(),
    meta: { suppressGlobalToast: true },
    onSuccess: (result) => {
      setLastResult(result);
      onSuccess();
    },
  });

  const { data: progress } = useQuery<DocumentBackfillProgress | null>({
    queryKey: ['peopleforce', 'documentsBackfillProgress'],
    queryFn: getDocumentsBackfillStatus,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 3000 : 15000),
    refetchIntervalInBackground: true,
  });

  // Fire onSuccess once on running → completed/partial transition (covers
  // the case where someone started a backfill in another tab / via curl).
  const prevStatusRef = useRef<DocumentBackfillProgress['status'] | null>(null);
  useEffect(() => {
    const cur = progress?.status ?? null;
    const prev = prevStatusRef.current;
    if (prev === 'running' && (cur === 'completed' || cur === 'partial')) {
      onSuccess();
    }
    prevStatusRef.current = cur;
  }, [progress?.status, onSuccess]);

  const isRunning = progress?.status === 'running' || mutation.isPending;
  const lastSync = history.find((h) => h.syncType === 'documents_migration');

  return (
    <>
      <Card className={cn('border-hygge/40 bg-hygge/5', isRunning && 'border-hygge/70')}>
        <CardHeader className="pb-2">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors',
                isRunning ? 'bg-hygge/30' : 'bg-hygge/15'
              )}
            >
              {isRunning ? (
                <Loader2 className="h-5 w-5 text-hygge animate-spin" />
              ) : (
                <Archive className="h-5 w-5 text-hygge" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                Employee Documents Backfill (encrypted)
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Downloads every employee's PeopleForce documents (passport scans, contracts, NDAs,
                etc.) and stores them <strong>encrypted</strong> on the server (AES-256-GCM + KMS
                envelope in prod, local secret in dev). Only super-admin / HR-admin can view; ops
                with raw S3/SSH access see ciphertext only. Idempotent — re-runs skip files already
                imported.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              className="ml-auto gap-1.5 cursor-pointer"
              disabled={isRunning}
              onClick={() => {
                setLastResult(null);
                setConfirmOpen(true);
              }}
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isRunning && 'animate-spin')} />
              {isRunning ? 'Backfilling…' : 'Start backfill'}
            </Button>
          </div>

          {progress && progress.status === 'running' && (
            <DocumentsBackfillProgressBar progress={progress} />
          )}

          {progress &&
            (progress.status === 'completed' || progress.status === 'partial') &&
            !lastResult && <DocumentsBackfillCompletedSummary progress={progress} />}

          {lastSync && !isRunning && !lastResult && !progress && (
            <p className="text-xs text-muted-foreground">
              Last run: {formatRelativeTime(lastSync.syncedAt)}
              {lastSync.status === 'success' && (
                <span className="text-green-600 ml-1">({lastSync.recordsProcessed} records)</span>
              )}
              {lastSync.status === 'partial' && (
                <span className="text-amber-600 ml-1">
                  ({lastSync.recordsProcessed} records, {lastSync.recordsFailed} errors)
                </span>
              )}
            </p>
          )}

          {lastResult && !mutation.isPending && (
            <div className="flex items-start gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
              <p className="text-xs text-green-700 dark:text-green-400 font-medium">
                {lastResult.message}
              </p>
            </div>
          )}

          {mutation.isError && !mutation.isPending && (
            <div className="flex items-start gap-1.5">
              <AlertCircle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
              <p className="text-xs text-destructive">
                {extractErrorMessage(mutation.error, 'Backfill failed')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-hygge" />
              <AlertDialogTitle>Backfill all employee documents from PeopleForce?</AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>For every employee with a PeopleForce link, this will:</p>
                <ul className="ml-5 list-disc space-y-1">
                  <li>List all their documents in PF and re-fetch a fresh presigned URL.</li>
                  <li>Download each file (PDF / image / docx).</li>
                  <li>
                    Encrypt locally (AES-256-GCM) and store ciphertext on the server. Only
                    super-admin / HR-admin can view via the UI; ops with S3 access see only
                    ciphertext.
                  </li>
                </ul>
                <p className="text-muted-foreground">
                  Runs on the server — safe to leave the page. Idempotent: files already imported
                  are skipped. May take 30-60 minutes for a full org.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                mutation.mutate();
              }}
            >
              Start backfill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function DocumentsBackfillProgressBar({ progress }: { progress: DocumentBackfillProgress }) {
  const {
    processed,
    total,
    durationMs,
    documentsCreated,
    documentsSkipped,
    documentsErrors,
    currentEmployee,
    startedAt,
  } = progress;
  const pct = total > 0 ? Math.min(100, (processed / total) * 100) : 0;
  const rateMs = processed > 0 ? durationMs / processed : 0;
  const remaining = Math.max(0, total - processed);
  const etaMs = rateMs > 0 ? rateMs * remaining : 0;
  const empsPerMin = rateMs > 0 ? Math.round(60_000 / rateMs) : 0;
  const elapsedMs = Date.now() - new Date(startedAt).getTime();

  return (
    <div className="space-y-2 rounded-md border border-hygge/30 bg-background/60 p-3">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-foreground">
          {processed.toLocaleString()}
          <span className="text-muted-foreground"> / {total.toLocaleString()} employees</span>
        </span>
        <span className="tabular-nums font-medium text-hygge">{pct.toFixed(1)}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-hygge/10">
        <div
          className="h-full bg-hygge transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
        <div>
          <div className="text-foreground tabular-nums">{formatDuration(elapsedMs)}</div>
          <div>elapsed</div>
        </div>
        <div>
          <div className="text-foreground tabular-nums">
            {etaMs > 0 ? formatDuration(etaMs) : '—'}
          </div>
          <div>ETA</div>
        </div>
        <div>
          <div className="text-foreground tabular-nums">
            {empsPerMin > 0 ? `${empsPerMin}/min` : '—'}
          </div>
          <div>throughput</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
          <span className="tabular-nums text-foreground">{documentsCreated}</span>
          <span className="text-muted-foreground">docs encrypted</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
          <span className="tabular-nums text-foreground">{documentsSkipped}</span>
          <span className="text-muted-foreground">skipped</span>
        </span>
        {documentsErrors > 0 && (
          <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3" />
            <span className="tabular-nums">{documentsErrors}</span>
            <span>errors</span>
          </span>
        )}
      </div>
      {currentEmployee && (
        <div className="truncate text-[11px] text-muted-foreground">
          Currently:{' '}
          <span className="text-foreground">{currentEmployee.workEmail ?? currentEmployee.id}</span>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        Safe to leave this page — the job continues on the server and this bar resumes when you come
        back.
      </p>
    </div>
  );
}

function DocumentsBackfillCompletedSummary({ progress }: { progress: DocumentBackfillProgress }) {
  const isPartial = progress.status === 'partial';
  return (
    <div className="flex items-start gap-1.5">
      <CheckCircle2
        className={cn(
          'mt-0.5 h-3.5 w-3.5 shrink-0',
          isPartial ? 'text-amber-600' : 'text-green-600'
        )}
      />
      <p
        className={cn(
          'text-xs font-medium',
          isPartial ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'
        )}
      >
        Documents backfill {isPartial ? 'finished with errors' : 'completed'}:{' '}
        {progress.documentsCreated} encrypted, {progress.documentsSkipped} skipped
        {progress.documentsErrors > 0 && `, ${progress.documentsErrors} errors`}. Scanned{' '}
        {progress.processed.toLocaleString()} employees over {formatDuration(progress.durationMs)}.
      </p>
    </div>
  );
}

function SyncHistorySection({ history }: { history: SyncHistoryEntry[] }) {
  if (history.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Sync History</CardTitle>
        <CardDescription>Recent synchronization operations</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {history.slice(0, 10).map((entry) => (
            <div
              key={entry.id}
              className="flex items-center gap-3 text-sm py-2 border-b border-border last:border-0"
            >
              <StatusBadge status={entry.status} />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-xs">{formatSyncType(entry.syncType)}</span>
                {entry.message && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{entry.message}</p>
                )}
              </div>
              <div className="text-xs text-muted-foreground shrink-0">
                {formatRelativeTime(entry.syncedAt)}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'success':
      return (
        <Badge
          variant="outline"
          className="text-green-600 border-green-600/30 bg-green-600/5 text-[10px] px-1.5"
        >
          Success
        </Badge>
      );
    case 'partial':
      return (
        <Badge
          variant="outline"
          className="text-amber-600 border-amber-600/30 bg-amber-600/5 text-[10px] px-1.5"
        >
          Partial
        </Badge>
      );
    case 'failed':
      return (
        <Badge
          variant="outline"
          className="text-destructive border-destructive/30 bg-destructive/5 text-[10px] px-1.5"
        >
          Failed
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-[10px] px-1.5">
          {status}
        </Badge>
      );
  }
}

function formatSyncType(type: string): string {
  const map: Record<string, string> = {
    employees_full_sync: 'Employees Sync',
    employee_import: 'Employee Import',
    avatar_migration: 'Avatar Sync',
    avatars_pull: 'Avatars Recovery (PF)',
    calendar_sync: 'Calendar Sync',
    absence_types_sync: 'Absence Types Sync',
    absence_balance_sync: 'Absence Balances Sync',
    notes_sync: 'Notes Sync',
    documents_sync: 'Documents Sync',
    projects_sync: 'Projects Sync',
    compensations_sync: 'Compensations Sync',
    additional_compensations_sync: 'Additional Compensations Sync',
    vacancies_sync: 'Vacancies Sync',
    candidates_sync: 'Candidates Sync',
  };
  return map[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// ============================================
// Preview Dialogs
// ============================================

const stateBadgeColors: Record<string, string> = {
  opened: 'text-green-600 border-green-600/30 bg-green-600/5',
  closed: 'text-gray-500 border-gray-500/30 bg-gray-500/5',
  held: 'text-amber-600 border-amber-600/30 bg-amber-600/5',
  archived: 'text-gray-400 border-gray-400/30 bg-gray-400/5',
  drafting: 'text-blue-500 border-blue-500/30 bg-blue-500/5',
  cancelled: 'text-red-500 border-red-500/30 bg-red-500/5',
};

function RecruitmentDataSection({
  onPreview,
}: {
  onPreview: (type: 'vacancies' | 'candidates') => void;
}) {
  const { data: counts } = useQuery({
    queryKey: ['peopleforce', 'recruitment-counts'],
    queryFn: getRecruitmentCounts,
    retry: false,
  });

  const hasData = counts && (counts.vacancies > 0 || counts.candidates > 0);

  if (!hasData) return null;

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Recruitment Data</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Synced recruitment data available for preview (PeopleForce sync not required)
        </p>
      </div>

      <Card>
        <CardContent className="flex items-center gap-4 py-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-hygge/10">
            <Briefcase className="h-5 w-5 text-hygge" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm">Vacancies</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {counts.vacancies.toLocaleString()} vacancies in database
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 cursor-pointer"
            onClick={() => onPreview('vacancies')}
          >
            <Eye className="h-3.5 w-3.5" />
            Preview
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-center gap-4 py-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-hygge/10">
            <UserSearch className="h-5 w-5 text-hygge" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm">Candidates</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {counts.candidates.toLocaleString()} candidates in database
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 cursor-pointer"
            onClick={() => onPreview('candidates')}
          >
            <Eye className="h-3.5 w-3.5" />
            Preview
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function PaginationBar({
  page,
  pages,
  total,
  onPageChange,
}: {
  page: number;
  pages: number;
  total: number;
  onPageChange: (p: number) => void;
}) {
  const maxVisible = 7;
  const pageNumbers: (number | '...')[] = [];

  if (pages <= maxVisible) {
    for (let i = 1; i <= pages; i++) pageNumbers.push(i);
  } else {
    pageNumbers.push(1);
    if (page > 3) pageNumbers.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(pages - 1, page + 1); i++) {
      pageNumbers.push(i);
    }
    if (page < pages - 2) pageNumbers.push('...');
    pageNumbers.push(pages);
  }

  return (
    <div className="flex items-center justify-between pt-3 border-t shrink-0">
      <span className="text-xs text-muted-foreground">
        {total.toLocaleString()} records -- page {page} of {pages}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Prev
        </Button>
        {pageNumbers.map((p, i) =>
          p === '...' ? (
            <span key={`dots-${i}`} className="px-1 text-xs text-muted-foreground">
              ...
            </span>
          ) : (
            <Button
              key={p}
              // A pagination pill: `secondary` marks the current page, which is
              // not a primary action and so does not take the mint [HW-926]
              variant={p === page ? 'secondary' : 'outline'}
              size="sm"
              className="min-w-[32px] h-8 px-2"
              onClick={() => onPageChange(p)}
            >
              {p}
            </Button>
          )
        )}
        <Button
          variant="outline"
          size="sm"
          disabled={page >= pages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function VacanciesPreviewDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['peopleforce', 'vacancies-preview', page, search],
    queryFn: () => getVacancies(page, 50, undefined, search || undefined),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[95vw] max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Synced Vacancies
            {data?.metadata && (
              <span className="text-sm font-normal text-muted-foreground">
                ({data.metadata.total.toLocaleString()} total)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="relative shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by title..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !data?.data.length ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No vacancies synced yet. Click "Sync" first.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="text-sm whitespace-nowrap">
                <thead className="sticky top-0 bg-background border-b">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="py-2 px-3 font-medium">PF ID</th>
                    <th className="py-2 px-3 font-medium">Title</th>
                    <th className="py-2 px-3 font-medium">State</th>
                    <th className="py-2 px-3 font-medium">Priority</th>
                    <th className="py-2 px-3 font-medium">Positions</th>
                    <th className="py-2 px-3 font-medium">Department</th>
                    <th className="py-2 px-3 font-medium">Location</th>
                    <th className="py-2 px-3 font-medium">Hiring Lead</th>
                    <th className="py-2 px-3 font-medium">Employment Type</th>
                    <th className="py-2 px-3 font-medium">Job Level</th>
                    <th className="py-2 px-3 font-medium">Min Salary</th>
                    <th className="py-2 px-3 font-medium">Max Salary</th>
                    <th className="py-2 px-3 font-medium">Currency</th>
                    <th className="py-2 px-3 font-medium">Apps</th>
                    <th className="py-2 px-3 font-medium">Pipeline</th>
                    <th className="py-2 px-3 font-medium">Tags</th>
                    <th className="py-2 px-3 font-medium">Skills</th>
                    <th className="py-2 px-3 font-medium">Collaborators</th>
                    <th className="py-2 px-3 font-medium">Careers</th>
                    <th className="py-2 px-3 font-medium">Opened At</th>
                    <th className="py-2 px-3 font-medium">Starts On</th>
                    <th className="py-2 px-3 font-medium">Ends On</th>
                    <th className="py-2 px-3 font-medium">PF Created</th>
                    <th className="py-2 px-3 font-medium">PF Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((v: VacancyPreview) => (
                    <tr key={v.id} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-2 px-3 text-muted-foreground font-mono text-xs">
                        {v.peopleForceId}
                      </td>
                      <td className="py-2 px-3 font-medium max-w-[300px] truncate">{v.title}</td>
                      <td className="py-2 px-3">
                        <Badge
                          variant="outline"
                          className={cn('text-[10px] px-1.5', stateBadgeColors[v.state] || '')}
                        >
                          {v.state}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">{v.priority || '-'}</td>
                      <td className="py-2 px-3 text-center">{v.positions}</td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {v.department?.name || '-'}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">{v.location?.name || '-'}</td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {v.hiringLead ? `${v.hiringLead.firstName} ${v.hiringLead.lastName}` : '-'}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">{v.employmentType || '-'}</td>
                      <td className="py-2 px-3 text-muted-foreground">{v.jobLevel || '-'}</td>
                      <td className="py-2 px-3 text-muted-foreground">{v.minSalary ?? '-'}</td>
                      <td className="py-2 px-3 text-muted-foreground">{v.maxSalary ?? '-'}</td>
                      <td className="py-2 px-3 text-muted-foreground">{v.currencyCode || '-'}</td>
                      <td className="py-2 px-3 text-center">{v.applicationsCount}</td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {(v.pipelineData as { name?: string })?.name || '-'}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {(v.tags as { name: string }[] | null)?.map((t) => t.name).join(', ') ||
                          '-'}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {(v.skills as { name: string }[] | null)?.map((s) => s.name).join(', ') ||
                          '-'}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {(v.collaborators as { first_name: string; last_name: string }[] | null)
                          ?.map((c) => `${c.first_name} ${c.last_name}`)
                          .join(', ') || '-'}
                      </td>
                      <td className="py-2 px-3 text-center">{v.postOnCareers ? 'Yes' : 'No'}</td>
                      <td className="py-2 px-3 text-muted-foreground text-xs">
                        {v.openedAt ? new Date(v.openedAt).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground text-xs">
                        {v.startsOn
                          ? formatDate(v.startsOn, { year: 'numeric', month: 'numeric', day: 'numeric' })
                          : '-'}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground text-xs">
                        {v.endsOn
                          ? formatDate(v.endsOn, { year: 'numeric', month: 'numeric', day: 'numeric' })
                          : '-'}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground text-xs">
                        {new Date(v.pfCreatedAt).toLocaleDateString()}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground text-xs">
                        {new Date(v.pfUpdatedAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {data?.metadata && data.metadata.pages > 1 && (
          <PaginationBar
            page={data.metadata.page}
            pages={data.metadata.pages}
            total={data.metadata.total}
            onPageChange={setPage}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CandidatesPreviewDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['peopleforce', 'candidates-preview', page, search],
    queryFn: () => getCandidates(page, 50, undefined, search || undefined),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[95vw] max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserSearch className="h-5 w-5" />
            Synced Candidates
            {data?.metadata && (
              <span className="text-sm font-normal text-muted-foreground">
                ({data.metadata.total.toLocaleString()} total)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="relative shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !data?.data.length ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No candidates synced yet. Sync vacancies first, then candidates.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="text-sm whitespace-nowrap">
                <thead className="sticky top-0 bg-background border-b">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="py-2 px-3 font-medium">PF ID</th>
                    <th className="py-2 px-3 font-medium">Full Name</th>
                    <th className="py-2 px-3 font-medium">Email</th>
                    <th className="py-2 px-3 font-medium">Phone</th>
                    <th className="py-2 px-3 font-medium">Source</th>
                    <th className="py-2 px-3 font-medium">Position</th>
                    <th className="py-2 px-3 font-medium">Location</th>
                    <th className="py-2 px-3 font-medium">Desired Salary</th>
                    <th className="py-2 px-3 font-medium">Currency</th>
                    <th className="py-2 px-3 font-medium">Resume</th>
                    <th className="py-2 px-3 font-medium">URLs</th>
                    <th className="py-2 px-3 font-medium">Skills</th>
                    <th className="py-2 px-3 font-medium">Languages</th>
                    <th className="py-2 px-3 font-medium">Tags</th>
                    <th className="py-2 px-3 font-medium">Vacancy</th>
                    <th className="py-2 px-3 font-medium">Stage</th>
                    <th className="py-2 px-3 font-medium">Disqualified</th>
                    <th className="py-2 px-3 font-medium">Recruiter</th>
                    <th className="py-2 px-3 font-medium">Last Contacted</th>
                    <th className="py-2 px-3 font-medium">PF Created</th>
                    <th className="py-2 px-3 font-medium">PF Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((c: CandidatePreview) => {
                    const firstApp = c.applications?.[0];
                    return (
                      <tr key={c.id} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 px-3 text-muted-foreground font-mono text-xs">
                          {c.peopleForceId}
                        </td>
                        <td className="py-2 px-3 font-medium">{c.fullName}</td>
                        <td className="py-2 px-3 text-muted-foreground">{c.email || '-'}</td>
                        <td className="py-2 px-3 text-muted-foreground">{c.phone || '-'}</td>
                        <td className="py-2 px-3 text-muted-foreground max-w-[150px] truncate">
                          {c.source || '-'}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">{c.position || '-'}</td>
                        <td className="py-2 px-3 text-muted-foreground">{c.location || '-'}</td>
                        <td className="py-2 px-3 text-muted-foreground">
                          {c.desiredSalary ?? '-'}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">{c.currencyCode || '-'}</td>
                        <td className="py-2 px-3 text-center">{c.hasResume ? 'Yes' : 'No'}</td>
                        <td className="py-2 px-3 text-muted-foreground max-w-[200px] truncate">
                          {(c.urls as string[] | null)?.join(', ') || '-'}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">
                          {(c.skills as { name: string }[] | null)?.map((s) => s.name).join(', ') ||
                            '-'}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">
                          {(c.languages as { name: string }[] | null)
                            ?.map((l) => l.name)
                            .join(', ') || '-'}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">
                          {(c.tags as { name: string }[] | null)?.map((t) => t.name).join(', ') ||
                            '-'}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground max-w-[200px] truncate">
                          {firstApp?.vacancy.title || '-'}
                          {c.applications.length > 1 && (
                            <span className="text-xs text-muted-foreground/70 ml-1">
                              +{c.applications.length - 1}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          {firstApp?.pipelineStageName ? (
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[10px] px-1.5',
                                firstApp.disqualifiedAt
                                  ? 'text-red-500 border-red-500/30 bg-red-500/5'
                                  : 'text-blue-500 border-blue-500/30 bg-blue-500/5'
                              )}
                            >
                              {firstApp.pipelineStageName}
                            </Badge>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground text-xs">
                          {firstApp?.disqualifiedAt
                            ? new Date(firstApp.disqualifiedAt).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">
                          {c.createdBy ? `${c.createdBy.firstName} ${c.createdBy.lastName}` : '-'}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground text-xs">
                          {c.lastContactedAt
                            ? new Date(c.lastContactedAt).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground text-xs">
                          {new Date(c.pfCreatedAt).toLocaleDateString()}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground text-xs">
                          {new Date(c.pfUpdatedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {data?.metadata && data.metadata.pages > 1 && (
          <PaginationBar
            page={data.metadata.page}
            pages={data.metadata.pages}
            total={data.metadata.total}
            onPageChange={setPage}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
